// SPEC-425 — canvas auto-layout engine (single source of truth).
// Pure TS, zero runtime dependencies: must not import react/konva/store or any web-internal
// module (textMetrics.ts is the sole exception — an equally pure SPEC-455 M1 sibling). Object
// shapes are declared structurally so both web (store.ts/Artboard.tsx) and the worker mirror
// (agent.ts applyOpLocally / buildCanvasBrief) can consume the same module.
//
// Semantics: every object with `parent` = <frame id> is laid out by its frame's `layout`
// params (row/column flow, gap, padding, align, justify). resolveLayout recomputes absolute
// x/y for parented objects and returns a new array; unparented objects pass through untouched
// (backward compat: no parent → byte-identical coordinates).
// Determinism criterion (SPEC-455 M1, replaces the old "child sizes are never mutated"
// invariant per spec v3 B3): re-running resolveLayout on the same input yields byte-identical
// output. Objects WITHOUT a `sizing` field keep byte-identical w/h (passthrough red line);
// objects WITH `sizing` get engine-derived sizes — text 'auto'/'fixed-width' measured via the
// shared font-metrics table, frame 'fit_content' hugged around children, 'fill_container'
// stretched into the parent's content box — all pure functions of the input, so recomputes
// are stable (idempotent). Nested frames are resolved top-down (a child frame is positioned
// by its parent before laying out its own children); parent cycles are broken defensively.

import { ceilPx, deriveTextSize, type TextMeasurer } from './textMetrics';

/** Minimal structural shape the engine needs (CanvasObject-compatible on both sides). */
export interface LayoutObjectLike {
  id: string;
  type: string;
  x: number;
  y: number;
  w: number;
  h: number;
  parent?: string;
  layout?: LayoutSpec;
  // SPEC-455 M1: inputs for engine-derived sizing (all optional; absent sizing = legacy
  // byte-identical w/h passthrough).
  text?: string;
  font_size?: number;
  font_weight?: string;
  sizing?: string;
  // SPEC-462 M2: layer positioning — 'absolute' = out-of-flow child (never enters the parent's
  // flow math; anchored to the parent's border-box). Absent = in-flow legacy behavior.
  position?: string;
  // SPEC-464 M1.4: icon 像素 snap 仅作用于无旋转 icon（旋转包围盒与像素网格相位关系不同）。
  rotation?: number;
}

/** Engine options (SPEC-455 M1). Both optional — omitted = physics on, table measurer. */
export interface LayoutOptions {
  /** Injectable single-line width measurer (px). Default = the shared font-metrics table. */
  measure?: TextMeasurer;
  /** false = legacy pre-455 path: only recompute x/y, never derive sizes (worker LAYOUT_PHYSICS=off kill-switch). */
  physics?: boolean;
  /** false = legacy pre-462 path: `position` fields ignored, every parented child flows
   * (worker LAYER_POSITIONING=off kill-switch). Omitted/true = M2 semantics on. */
  layerPositioning?: boolean;
  /** true = SPEC-464 M1.4: 无旋转 icon 的 x/y 取整（像素 snap — 描边骑网格 AA 模糊根治）。
   * 函数级缺省 off（imageSlot 同款刻意不对称范式）：省略/false = 旧分数坐标直通
   * byte-identical（worker ICON_OPTICAL=off kill-switch 面）；web 渲染侧由 store.ts
   * WEB_LAYOUT_OPTS 显式恒传 true（web 无 env 通道，勿发明机制 — 三方一致红线）。 */
  iconSnap?: boolean;
}

/** Frame auto-layout parameters (mirrors FrameLayout in types.ts on both sides). */
export interface LayoutSpec {
  direction: 'row' | 'column';
  gap?: number; // default 8
  padding?: number | [number, number, number, number]; // number or [t,r,b,l]; default 0
  align?: 'start' | 'center' | 'end'; // cross axis; default 'start'
  justify?: 'start' | 'center' | 'end' | 'space-between'; // main axis; default 'start'
}

/** Normalize padding to [t,r,b,l] numbers (defensive: bad values → 0). */
export function normalizePadding(p: LayoutSpec['padding']): [number, number, number, number] {
  if (typeof p === 'number' && Number.isFinite(p)) return [p, p, p, p];
  if (Array.isArray(p) && p.length === 4 && p.every(v => typeof v === 'number' && Number.isFinite(v))) {
    return [p[0], p[1], p[2], p[3]];
  }
  return [0, 0, 0, 0];
}

const num = (v: unknown, dflt: number): number => (typeof v === 'number' && Number.isFinite(v) ? v : dflt);

/** True when any object participates in auto-layout (parented child, frame, or engine sizing — SPEC-455 M1) — legacy canvases skip resolveLayout entirely (byte-identical passthrough). */
export function hasLayout<T extends LayoutObjectLike>(objects: readonly T[]): boolean {
  return objects.some(o => o.parent !== undefined || o.type === 'frame' || o.sizing !== undefined);
}

/** Ids of all transitive children of rootId (parent-link walk, cycle-safe; root itself excluded). Used for cascade delete of frames. */
export function collectDescendants<T extends LayoutObjectLike>(objects: readonly T[], rootId: string): string[] {
  const kidsOf = new Map<string, string[]>();
  for (const o of objects) {
    if (o.parent) {
      const arr = kidsOf.get(o.parent);
      if (arr) arr.push(o.id);
      else kidsOf.set(o.parent, [o.id]);
    }
  }
  const out: string[] = [];
  const seen = new Set<string>([rootId]);
  const stack = [rootId];
  while (stack.length > 0) {
    const cur = stack.pop() as string;
    for (const kid of kidsOf.get(cur) ?? []) {
      if (!seen.has(kid)) { seen.add(kid); out.push(kid); stack.push(kid); }
    }
  }
  return out;
}

/**
 * Recompute absolute x/y of all parented objects from their frame's layout params, and —
 * SPEC-455 M1, physics on — derive sizes of `sizing`-carrying objects first (bottom-up:
 * text from the shared measurer, fit_content frames from children extents; top-down during
 * the flow pass: fill_container from the parent's content box). Objects without `sizing`
 * keep byte-identical w/h. Returns a new array of shallow copies; input is never mutated.
 */
export function resolveLayout<T extends LayoutObjectLike>(objects: readonly T[], options?: LayoutOptions): T[] {
  const physics = options?.physics !== false;
  // SPEC-462 M2 kill-switch: layerPositioning=false → position 字段整体忽略（旧路径字节一致）。
  const layerPos = options?.layerPositioning !== false;
  const out: T[] = objects.map(o => ({ ...o }));
  const byId = new Map<string, T>();
  for (const o of out) byId.set(o.id, o);
  const childrenOf = new Map<string, T[]>();
  for (const o of out) {
    if (o.parent && byId.has(o.parent)) {
      const arr = childrenOf.get(o.parent);
      if (arr) arr.push(o);
      else childrenOf.set(o.parent, [o]);
    }
  }
  if (physics) {
    // Pass 1 (bottom-up, post-order): derive sizes so the flow pass sees final geometry.
    const sized = new Set<string>();
    const deriving = new Set<string>(); // cycle guard
    const deriveNode = (o: T): void => {
      if (sized.has(o.id) || deriving.has(o.id)) return;
      deriving.add(o.id);
      if (o.type === 'text' && (o.sizing === 'auto' || o.sizing === 'fixed-width')) {
        const d = deriveTextSize(o.text ?? '', num(o.font_size, 48), o.font_weight, o.sizing, o.w, options?.measure);
        o.w = d.w;
        o.h = d.h;
      } else if (o.type === 'frame') {
        const kids = childrenOf.get(o.id) ?? [];
        for (const kid of kids) deriveNode(kid);
        if (o.sizing === 'fit_content') {
          const spec: LayoutSpec = o.layout ?? { direction: 'row' };
          const isRow = spec.direction !== 'column';
          const gap = Math.max(0, num(spec.gap, 8));
          const [pt, pr, pb, pl] = normalizePadding(spec.padding);
          // SPEC-462 M2: abs 子不占流位 → 也不参与 fit_content 的内在尺寸（CSS 绝对定位子
          // 不影响容器 intrinsic size 同源语义）。layerPos off → 旧路径（全部 kids 参与）。
          const flowKids = layerPos ? kids.filter(k => k.position !== 'absolute') : kids;
          const n = flowKids.length;
          const mainSum = flowKids.reduce((s, k) => s + (isRow ? k.w : k.h), 0) + (n > 1 ? gap * (n - 1) : 0);
          let crossMax = 0;
          for (const k of flowKids) crossMax = Math.max(crossMax, isRow ? k.h : k.w);
          o.w = ceilPx((isRow ? mainSum : crossMax) + pl + pr);
          o.h = ceilPx((isRow ? crossMax : mainSum) + pt + pb);
        }
      }
      deriving.delete(o.id);
      sized.add(o.id);
    };
    for (const o of out) deriveNode(o);
  }
  const laying = new Set<string>(); // cycle guard
  const layoutFrame = (frameId: string): void => {
    const frame = byId.get(frameId);
    if (!frame || frame.type !== 'frame' || laying.has(frameId)) return;
    laying.add(frameId);
    const allKids = childrenOf.get(frameId) ?? [];
    // SPEC-462 M2: flow pass 只看流内子 — abs 子从 kids 滤除（不占流位、不进 usedMain/gap/
    // justify/fill_container 计算）。layerPos off → 旧路径（position 字段忽略，全员进流）。
    const kids = layerPos ? allKids.filter(k => k.position !== 'absolute') : allKids;
    const spec: LayoutSpec = frame.layout ?? { direction: 'row' };
    const isRow = spec.direction !== 'column';
    const gap = Math.max(0, num(spec.gap, 8));
    const [pt, pr, pb, pl] = normalizePadding(spec.padding);
    const justify = spec.justify ?? 'start';
    const align = spec.align ?? 'start';
    // Content box (frame interior after padding), in absolute coords.
    const boxX = frame.x + pl;
    const boxY = frame.y + pt;
    const boxW = Math.max(0, frame.w - pl - pr);
    const boxH = Math.max(0, frame.h - pt - pb);
    const main = (o: T): number => (isRow ? o.w : o.h);
    const cross = (o: T): number => (isRow ? o.h : o.w);
    const boxMain = isRow ? boxW : boxH;
    const boxCross = isRow ? boxH : boxW;
    const n = kids.length;
    if (physics) {
      // SPEC-455 M1: fill_container children resolve against this frame's content box before
      // the flow: main axis = equal floored share of the free space left by non-fill children
      // (a filled child can therefore never exceed its parent), cross axis = stretched.
      const fills = kids.filter(k => k.sizing === 'fill_container');
      if (fills.length > 0) {
        const fillIds = new Set(fills.map(k => k.id));
        const totalGap = n > 1 ? gap * (n - 1) : 0;
        let fixedMain = 0;
        for (const k of kids) if (!fillIds.has(k.id)) fixedMain += main(k);
        const share = Math.floor(Math.max(0, boxMain - fixedMain - totalGap) / fills.length);
        const crossSize = Math.floor(boxCross);
        for (const f of fills) {
          if (isRow) { f.w = share; f.h = crossSize; }
          else { f.h = share; f.w = crossSize; }
        }
      }
    }
    const usedMain = kids.reduce((s, o) => s + main(o), 0) + (n > 1 ? gap * (n - 1) : 0);
    const free = boxMain - usedMain;
    let lead = 0; // main-axis offset before the first child
    let extra = 0; // additional main-axis spacing injected between children (space-between)
    if (n > 0) {
      if (justify === 'center') lead = free / 2;
      else if (justify === 'end') lead = free;
      else if (justify === 'space-between' && n > 1) extra = free / (n - 1);
    }
    let cursor = (isRow ? boxX : boxY) + lead;
    for (const kid of kids) {
      const cFree = boxCross - cross(kid);
      const cOff = align === 'center' ? cFree / 2 : align === 'end' ? cFree : 0;
      if (isRow) {
        kid.x = cursor;
        kid.y = boxY + cOff;
        cursor += kid.w + gap + extra;
      } else {
        kid.x = boxX + cOff;
        kid.y = cursor;
        cursor += kid.h + gap + extra;
      }
      if (kid.type === 'frame') layoutFrame(kid.id); // nested: resolve after position is final
    }
    if (layerPos) {
      // SPEC-462 M2: abs 子锤定父 border-box（flow pass 之后，z 序 = 数组序不变）。声明 w/h
      // 尊重（封面/徽标/浮动按钮/水印尺寸由声明方给）；x/y 锤入 border-box 可达窗口
      // [frame.x, frame.x + max(0, frame.w − w)]（与 applier move 的画布 clamp 同源范式）：
      // 全覆盖子（w=frame.w，背景图槽缺省 = 落槽卡尺发 border-box 全覆盖）窗口退化为原点 →
      // 逐父精确跟随；clamp 幂等（双跑不动点 = 确定性红线），且 abs 子的存在不改任何
      // 兄弟坐标（已滤出流计算）。显式偏移在发射层解释（tools.ts：op.x = frame.x + 偏移）。
      for (const kid of allKids) {
        if (kid.position !== 'absolute') continue;
        const maxX = frame.x + Math.max(0, frame.w - kid.w);
        const maxY = frame.y + Math.max(0, frame.h - kid.h);
        kid.x = Math.min(Math.max(kid.x, frame.x), maxX);
        kid.y = Math.min(Math.max(kid.y, frame.y), maxY);
        if (kid.type === 'frame') layoutFrame(kid.id); // nested abs frame: resolve after anchor
      }
    }
    laying.delete(frameId);
  };
  for (const o of out) if (o.type === 'frame') layoutFrame(o.id);
  if (options?.iconSnap === true) {
    // SPEC-464 M1.4: icon 像素 snap — 仅无旋转 icon 的 x/y 取整（text/image/rect 一律不动 —
    // M1 范围红线）。置于全部 layout pass 之后：流内/脱流坐标一并落格；不进 flow math、
    // 不改 w/h 与任何兄弟坐标；幂等（整数经 Math.round 不动点 = 双跑确定性红线不破）。
    for (const o of out) {
      if (o.type !== 'icon' || (o.rotation ?? 0) !== 0) continue;
      o.x = Math.round(o.x);
      o.y = Math.round(o.y);
    }
  }
  return out;
}
